import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SolicitudService } from '../../../../services/database/pqrsd/solicitud.service';
import { IGetSolicitudesGlobales, IGetSolicitudesPorTipoPqrsd } from '../../../../shared/interfaces/database/pqrsd';
import { ChartBarComponent, ChartBarConfig } from '../../components/chart-bar/chart-bar.component';
import {
  ChartDonutComponent,
  ChartDonutConfig,
  ChartDonutSlice
} from '../../components/chart-donut/chart-donut.component';
import { ChartLineComponent, ChartLineConfig } from '../../components/chart-line/chart-line.component';
import {
  ChartRadialGaugeComponent,
  ChartRadialGaugeConfig
} from '../../components/chart-radial-gauge/chart-radial-gauge.component';
import {HEX_COLOR_PALETTE} from "../../components/utils/color-palette";

@Component({
  selector: 'app-dashboard-general-pqrsdf',
  standalone: true,
  imports: [
    TranslatePipe,
    ChartBarComponent,
    ChartDonutComponent,
    ChartLineComponent,
    ChartRadialGaugeComponent
  ],
  templateUrl: './dashboard-general-pqrsdf.component.html',
  styleUrl: './dashboard-general-pqrsdf.component.scss'
})
export class DashboardGeneralPqrsdfComponent implements OnInit, AfterViewInit, OnChanges {

  @Input() fechaInicial!: Date;
  @Input() fechaFinal!: Date;
  @Input() solicitudesRecibidas: string = '';

  @ViewChild('chartBarTipo') chartBarTipo!: ChartBarComponent;
  @ViewChild('chartDonutTotal') chartDonutComponent!: ChartDonutComponent;
  @ViewChild('chartLineHistorico') chartLineHistorico!: ChartLineComponent;
  @ViewChild('chartRadialGaugeEfectividad') chartRadialGaugeEfectividad!: ChartRadialGaugeComponent;

  barTipoConfig!: ChartBarConfig;
  chartDonutConfig!: ChartDonutConfig;
  lineHistoricoConfig!: ChartLineConfig;
  radialGaugeEfectividadConfig!: ChartRadialGaugeConfig;

  private solicitudesTipoData: IGetSolicitudesPorTipoPqrsd[] = [];
  private globalSolicitudesData: IGetSolicitudesGlobales[] = [];
  private rawGlobalSolicitudesData: IGetSolicitudesGlobales[] = [];
  private historicoMesData: any[] = [];

  colors = HEX_COLOR_PALETTE;

  constructor(private solicitudService: SolicitudService) {
    this.initBarTipoConfig();
    this.initChartDonutConfig();
    this.initLineHistoricoConfig();
    this.initRadialGaugeEfectividadConfig();
  }

  private initBarTipoConfig(): void {
    this.barTipoConfig = {
      chartId: 'chart-solicitudes-tipo',
      categoryField: 'tipo',
      showLegend: false,
      barHeight: 38,
      xLabelFormatter: (v: number) => `${v}`,
      tooltipValueFormatter: (v: number) => `${v}`,
      bars: [
        { field: 'recibidos', name: 'Recibidos', color: this.colors[2], showValueLabel: true },
        { field: 'pendientes', name: 'Pendientes', color: this.colors[1], showValueLabel: true },
        { field: 'finalizados', name: 'Finalizados', color: this.colors[0], showValueLabel: true },
      ],
      data: [],
    };
  }

  private initChartDonutConfig(): void {
    this.chartDonutConfig = {
      chartId: 'donut-global-solicitudes-pqrsdf',
      slices: [],
      centerLabel: '',
    };
  }

  private initRadialGaugeEfectividadConfig(): void {
    this.radialGaugeEfectividadConfig = {
      chartId: 'radial-gauge-efectividad-pqrsdf',
      percentage: 0,
      centerSub: '0 / 0',
      height: 240,
    };
  }

  private initLineHistoricoConfig(): void {
    this.lineHistoricoConfig = {
      chartId: 'chart-historico-solicitudes',
      xLabelRotation:45,
      categoryField: 'mes',
      series: [
        { field: 'valor', name: 'Solicitudes', color: this.colors[0]},
      ],
      data: [],
    };
  }

  private loadChartDonut(data: IGetSolicitudesGlobales[]): void {
    if (!this.chartDonutComponent) return;
    const value: ChartDonutSlice[] = data.map((item, idx) => ({
      label: item.tipo,
      value: item.total,
      color: this.colors[idx % this.colors.length],
    }));
    const porcentaje = data?.find(it=> it.tipo.includes("finalizados"));

    this.chartDonutConfig.centerLabel = porcentaje ? `${porcentaje.total}` :'';
    this.chartDonutConfig.centerSub = 'Finalizados';
    this.chartDonutComponent.load(value);
  }

  private loadEfectividadGlobal(values: IGetSolicitudesGlobales[]): void {
    if (!this.chartRadialGaugeEfectividad) return;
    const recibidos = values.find(it => it.tipo === 'recibidos');
    const finalizados = values.find(it => it.tipo === 'finalizados');
    const recibidosTotal = recibidos?.total ?? 0;
    const finalizadosTotal = finalizados?.total ?? 0;
    const pct = recibidosTotal > 0 ? (finalizadosTotal / recibidosTotal) * 100 : 0;
    const pctRounded = +(pct.toFixed(1));

    this.chartRadialGaugeEfectividad.load({
      percentage: pctRounded,
      centerLabel: `${pctRounded}%`,
      centerSub: `${finalizadosTotal} / ${recibidosTotal} \n Solicitudes`,
    });
  }

  ngOnInit(): void {
    this.callDataSets();
  }

  ngAfterViewInit(): void {
    if (this.solicitudesTipoData?.length && this.chartBarTipo) {
      this.chartBarTipo.load(this.solicitudesTipoData);
    }
    if (this.globalSolicitudesData?.length && this.chartDonutComponent) {
      this.loadChartDonut(this.globalSolicitudesData);
    }
    if (this.rawGlobalSolicitudesData?.length && this.chartRadialGaugeEfectividad) {
      this.loadEfectividadGlobal(this.rawGlobalSolicitudesData);
    }
    if (this.historicoMesData?.length && this.chartLineHistorico) {
      this.chartLineHistorico.load(this.historicoMesData);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isFirstChange = Object.values(changes).every(c => c.isFirstChange());
    if (!isFirstChange) {
      this.callDataSets();
    }
  }

  // ─── Data loading methods ───────────────────────────────────────────────────

  getSolicitudesByTipoPQRSD(): void {
    firstValueFrom(
      this.solicitudService.getSolicitudByTipoPQRSD(
        this.castToString(this.fechaInicial),
        this.castToString(this.fechaFinal)
      )
    ).then(response => {
      if (!response) return;
      const mapped = response.map(f => ({
        ...f,
        tipo: f.tipo?.toUpperCase(),
      }));
      this.solicitudesTipoData = mapped;
      this.chartBarTipo?.load(mapped);
    }).catch(error => {
      console.error(error);
    });
  }

  getSolicitudesGlobales(): void {
    const startDate = this.castToString(this.fechaInicial);
    const endDate = this.castToString(this.fechaFinal);

    firstValueFrom(
      this.solicitudService.getSolicitudesGlobalesByRangoFecha(startDate, endDate)
    ).then(value => {
      if (!value) return;
      this.rawGlobalSolicitudesData = value;
      const data: IGetSolicitudesGlobales[] = value.filter(item => item.tipo !== 'recibidos');
      this.globalSolicitudesData = data;
      this.loadChartDonut(data);
      this.loadEfectividadGlobal(value);
    }).catch(error => {
      console.error(error);
    });
  }

  getSolicitudesByMes(): void {
    firstValueFrom(
      this.solicitudService.getSolicitudesByMesByRangoFecha(
        this.castToString(this.fechaInicial),
        this.castToString(this.fechaFinal)
      )
    ).then(response => {
      if (!response) return;
      const mapped = response.map(item => ({
        mes: item.mes,
        valor: (item as any).valor ?? (item as any).value ?? 0,
      }));
      this.historicoMesData = mapped;
      this.chartLineHistorico?.load(mapped);
    }).catch(error => {
      console.error(error);
    });
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  castToString(date: Date): string {
    return date.toISOString().substring(0, date.toISOString().indexOf('T'));
  }

  callDataSets(): void {
    this.getSolicitudesByMes();
    this.getSolicitudesGlobales();
    this.getSolicitudesByTipoPQRSD();
  }
}
